import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGoToRegister: () => void;
}

export default function AuthModal({ isOpen, onClose, onGoToRegister }: AuthModalProps) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [mode, setMode] = useState<'login' | 'forgot' | 'otp'>('login');
  const [otpCode, setOtpCode] = useState('');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setError('');
      setForgotSent(false);
      setMode('login');
      setEmail('');
      setOtpCode('');
      setOtpSent(false);
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/dashboard` },
    });
    if (err) setError(err.message);
    setLoading(false);
  };

  const handleLoginWithEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const { error: err } = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: false },
    });
    if (err) {
      setError('No encontramos ese nodo en la red. ¿Quieres crear una cuenta?');
    } else {
      setOtpSent(true);
      setMode('otp');
    }
    setLoading(false);
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const { error: err } = await supabase.auth.verifyOtp({
      email,
      token: otpCode,
      type: 'email',
    });
    if (err) {
      setError('Clave incorrecta o expirada. Verifica tu correo.');
    } else {
      onClose();
      navigate('/dashboard');
    }
    setLoading(false);
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    await supabase.auth.signInWithOtp({ email });
    setLoading(false);
    setForgotSent(true);
  };

  const handleRegister = () => {
    onClose();
    onGoToRegister();
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center px-4"
      style={{ background: 'rgba(26,28,32,0.88)', backdropFilter: 'blur(12px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative w-full max-w-md bg-[#1A1C20] rounded-2xl overflow-hidden"
        style={{ border: '1px solid rgba(139,157,131,0.25)' }}
      >
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.04]"
          style={{
            backgroundImage: 'linear-gradient(rgba(139,157,131,1) 1px, transparent 1px), linear-gradient(90deg, rgba(139,157,131,1) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />

        {/* Top bar */}
        <div className="relative flex items-center justify-between px-8 pt-8 pb-6 border-b border-[#F8F6F3]/8">
          <div>
            <p className="text-[#8B9D83] text-[10px] uppercase tracking-[3px] mb-1">— Portal de Acceso —</p>
            <h2 className="text-[#F8F6F3] font-bold text-2xl" style={{ letterSpacing: '-0.5px' }}>
              {mode === 'login' && 'Reconexión Nodal'}
              {mode === 'forgot' && 'Recuperar Frecuencia'}
              {mode === 'otp' && 'Validar Frecuencia'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F8F6F3]/5 hover:bg-[#F8F6F3]/12 text-[#F8F6F3]/60 hover:text-[#F8F6F3] transition-all duration-200 cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="relative px-8 py-8">
          {/* LOGIN */}
          {mode === 'login' && (
            <div className="space-y-5">
              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 bg-[#F8F6F3] hover:bg-white text-[#2A2D34] font-semibold text-sm py-3.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60"
                style={{ border: '1px solid rgba(42,45,52,0.15)' }}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M17.64 9.20455C17.64 8.56636 17.5827 7.95273 17.4764 7.36364H9V10.845H13.8436C13.635 11.97 13.0009 12.9232 12.0477 13.5614V15.8195H14.9564C16.6582 14.2527 17.64 11.9455 17.64 9.20455Z" fill="#4285F4"/><path d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5614C11.2418 14.1014 10.2109 14.4205 9 14.4205C6.65591 14.4205 4.67182 12.8373 3.96409 10.71H0.957275V13.0418C2.43818 15.9832 5.48182 18 9 18Z" fill="#34A853"/><path d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 9C3.68182 8.40682 3.78409 7.83 3.96409 7.29V4.95818H0.957275C0.347727 6.17318 0 7.54773 0 9C0 10.4523 0.347727 11.8268 0.957275 13.0418L3.96409 10.71Z" fill="#FBBC05"/><path d="M9 3.57955C10.3214 3.57955 11.5077 4.03364 12.4405 4.92545L15.0218 2.34409C13.4632 0.891818 11.4259 0 9 0C5.48182 0 2.43818 2.01682 0.957275 4.95818L3.96409 7.29C4.67182 5.16273 6.65591 3.57955 9 3.57955Z" fill="#EA4335"/></svg>
                Continuar con Google
              </button>

              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-[#F8F6F3]/10" />
                <span className="text-[#F8F6F3]/25 text-[10px] uppercase tracking-[2px]">o con email</span>
                <div className="flex-1 h-px bg-[#F8F6F3]/10" />
              </div>

              <form onSubmit={handleLoginWithEmail} className="space-y-5">
                <div>
                  <label className="block text-[#F8F6F3]/50 text-[10px] uppercase tracking-[2.5px] mb-2">Correo Electrónico</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@correo.com"
                    className="w-full bg-[#2A2D34] text-[#F8F6F3] text-sm px-4 py-3.5 rounded-lg border border-[#F8F6F3]/10 focus:outline-none focus:border-[#8B9D83] placeholder-[#F8F6F3]/20 transition-colors duration-200"
                  />
                </div>

                <button type="button" onClick={() => setMode('forgot')} className="text-[#C97B63] text-xs hover:underline cursor-pointer">
                  ¿Olvidaste tu frecuencia de acceso?
                </button>

                {error && (
                  <div className="bg-[#C97B63]/10 border border-[#C97B63]/30 rounded-lg px-4 py-3">
                    <p className="text-[#C97B63] text-xs leading-relaxed">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#8B9D83] text-[#1A1C20] font-bold text-sm py-4 rounded-xl hover:bg-[#7c8f74] transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60 cursor-pointer whitespace-nowrap"
                >
                  {loading ? <><i className="ri-loader-4-line animate-spin" /> Verificando...</> : <><i className="ri-login-box-line" /> Acceder al Control Center</>}
                </button>

                <div className="text-center pt-2">
                  <p className="text-[#F8F6F3]/30 text-xs mb-2">¿Aún no tienes nodo activo?</p>
                  <button type="button" onClick={handleRegister} className="text-[#C97B63] text-xs font-semibold hover:text-[#e08b73] underline underline-offset-2 cursor-pointer">
                    Iniciar mi Trazado → Crear Cuenta
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* OTP VERIFY */}
          {mode === 'otp' && (
            <form onSubmit={handleVerifyOtp} className="space-y-5">
              <div className="text-center mb-2">
                <div className="w-12 h-12 flex items-center justify-center rounded-full mx-auto mb-4" style={{ border: '1px solid rgba(139,157,131,0.4)', background: 'rgba(139,157,131,0.08)' }}>
                  <i className="ri-mail-send-line text-[#8B9D83] text-xl" />
                </div>
                <p className="text-[#F8F6F3]/60 text-sm leading-relaxed">
                  Hemos enviado tu <span className="text-[#8B9D83]">Clave de Alineación</span> a <span className="text-[#F8F6F3]/80">{email}</span>
                </p>
              </div>

              <div>
                <label className="block text-[#F8F6F3]/50 text-[10px] uppercase tracking-[2.5px] mb-2">Código de 6 dígitos</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  required
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  className="w-full bg-[#2A2D34] text-[#F8F6F3] text-xl font-bold px-4 py-3.5 rounded-lg border border-[#F8F6F3]/10 focus:outline-none focus:border-[#8B9D83] placeholder-[#F8F6F3]/20 transition-colors duration-200 text-center tracking-[0.4em]"
                  style={{ letterSpacing: '0.4em', border: otpCode.length === 6 ? '1px solid rgba(139,157,131,0.7)' : undefined }}
                />
              </div>

              {error && (
                <div className="bg-[#C97B63]/10 border border-[#C97B63]/30 rounded-lg px-4 py-3">
                  <p className="text-[#C97B63] text-xs">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || otpCode.length < 6}
                className="w-full font-bold text-sm py-4 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap disabled:cursor-not-allowed"
                style={{ background: otpCode.length === 6 ? '#8B9D83' : 'rgba(139,157,131,0.2)', color: otpCode.length === 6 ? '#1A1C20' : 'rgba(248,246,243,0.3)' }}
              >
                {loading ? <><i className="ri-loader-4-line animate-spin" /> Validando...</> : <><i className="ri-key-2-line" /> Validar Frecuencia de Nodo</>}
              </button>

              <button type="button" onClick={() => setMode('login')} className="w-full text-[#F8F6F3]/30 text-xs hover:text-[#F8F6F3]/60 cursor-pointer flex items-center justify-center gap-1">
                <i className="ri-arrow-left-line" /> Volver
              </button>
            </form>
          )}

          {/* FORGOT */}
          {mode === 'forgot' && (
            <form onSubmit={handleForgot} className="space-y-5">
              <p className="text-[#F8F6F3]/50 text-sm leading-relaxed">Introduce tu correo y te enviaremos una clave de restauración de frecuencia.</p>
              <div>
                <label className="block text-[#F8F6F3]/50 text-[10px] uppercase tracking-[2.5px] mb-2">Correo Electrónico</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@correo.com"
                  className="w-full bg-[#2A2D34] text-[#F8F6F3] text-sm px-4 py-3.5 rounded-lg border border-[#F8F6F3]/10 focus:outline-none focus:border-[#8B9D83] placeholder-[#F8F6F3]/20 transition-colors duration-200"
                />
              </div>

              {forgotSent ? (
                <div className="bg-[#8B9D83]/10 border border-[#8B9D83]/30 rounded-lg px-4 py-3">
                  <p className="text-[#8B9D83] text-xs leading-relaxed">✦ Si tu nodo existe en la red, recibirás las instrucciones en tu correo.</p>
                </div>
              ) : (
                <button type="submit" disabled={loading} className="w-full bg-[#C97B63] text-[#F8F6F3] font-semibold text-sm py-4 rounded-xl hover:bg-[#b86d55] transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60 cursor-pointer whitespace-nowrap">
                  {loading ? <><i className="ri-loader-4-line animate-spin" /> Enviando...</> : <>Restaurar Frecuencia</>}
                </button>
              )}

              <button type="button" onClick={() => setMode('login')} className="text-[#F8F6F3]/40 text-xs hover:text-[#F8F6F3]/70 cursor-pointer flex items-center gap-1">
                <i className="ri-arrow-left-line" /> Volver al acceso
              </button>
            </form>
          )}
        </div>

        <div className="px-8 py-4 border-t border-[#F8F6F3]/5 flex items-center justify-center gap-2">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-shield-keyhole-line text-[#8B9D83] text-xs" />
          </div>
          <p className="text-[#F8F6F3]/20 text-[10px] uppercase tracking-[2px]">Acceso seguro · Nube Geodésica</p>
        </div>
      </div>
    </div>
  );
}
